import React, { useEffect, useState } from 'react'
import Spinner from '../components/Spinner'
import { callAPI } from '../hooks/api'
import { useAuth } from '../hooks/getAuth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from 'react-circular-progressbar'
import { IoMdDownload } from 'react-icons/io'
import Dropdown from 'react-dropdown'
import { browserName, isMobile } from 'react-device-detect'
import BrowserCheck from '../components/BrowserCheck'
import InfoPopup from '../components/InfoPopup'
import { FaCircleInfo } from 'react-icons/fa6'
import '../assets/css/reports.css'
import { useRef } from 'react'
import { useRefSize } from '@/hooks/use-ref-size'
import UpgradeLayout from '@/components/upgrade-layout'
import useAbility from '@/hooks/use-ability'
import { isEmpty } from 'lodash'
import UpgradeButtonLayout from '@/components/upgrade-button-layout'
import UpgradeLockIconLayout from '@/components/upgrade-lock-icon-layout'

export default function Reports({ currentTab, setCurrentTab, localAuth }) {
  const { checkAbility } = useAbility()
  const [searchParams, setSearchParams] = useSearchParams()
  const removeStylesheet = (href) => {
    const link = document.querySelector(`link[href="${href}"]`)
    if (link) {
      document.body.removeChild(link)
    }
  }
  removeStylesheet('/css/landing.css')
  removeStylesheet('/css/bootstrap.css')
  const [loading, setLoading] = useState(false)
  const [ranchReports, setRanchReports] = useState([])
  const [selectedReports, setSelectedReports] = useState({})
  const { auth, setAuth, setIsLoggedIn } = useAuth()
  const [popupContent, setPopupContent] = useState(null)

  const navigate = useNavigate()

  function logout() {
    // TODO : signOut
    localStorage.setItem('auth', JSON.stringify({}))
    setAuth(null)
    setIsLoggedIn(false)
    navigate(
      '/' +
        (searchParams.get('locations')
          ? '?locations=' + searchParams.get('locations')
          : '')
    )
  }

  function onReportDropdownChange(e, ranchReport) {
    const report = ranchReport.reports.find((r) => r.created_time === e.value)
    setSelectedReports((prevSelectedReports) => ({
      ...prevSelectedReports,
      [ranchReport.ranch_id]: {
        ...prevSelectedReports[ranchReport.ranch_id],
        ...report,
      },
    }))
  }

  function getRanches() {
    var endpoint = '/get_all_reports'
    var params = {
      session_key: localAuth.sessionKey,
    }
    setLoading(true)
    callAPI(endpoint, params).then((res) => {
      setLoading(false)
      if (res.status === 'fail') {
        toast.warn(res.msg)
        return
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        logout()
        return
      }
      // Create a map to group reports by ranch_id
      const ranchReportsMap = new Map()

      res.reports.map((item) => {
        const { ranch_id, ranch_name, ...report } = item

        // If ranch_id is not in the map, create a new entry
        if (!ranchReportsMap.has(ranch_id)) {
          ranchReportsMap.set(ranch_id, { ranch_id, ranch_name, reports: [] })
        }

        // Add the report to the corresponding ranch_id
        ranchReportsMap.get(ranch_id).reports.push(report)
      })

      // Convert the map values to an array
      const transformedData = [...ranchReportsMap.values()]
      transformedData.sort((a, b) => b.ranch_id - a.ranch_id)
      // Sort reports by created_time within each ranch
      transformedData.map((ranch) => {
        ranch.reports.sort((a, b) => b.created_time - a.created_time)
      })

      var selectedReports = {}
      transformedData.map((ranch) => {
        selectedReports[ranch.ranch_id] = ranch.reports[0]
      })
      setSelectedReports(selectedReports)
      setRanchReports(transformedData)
    })
  }

  const enableRunReport = checkAbility('report_generation')
  const enableHydraScore = checkAbility('hydra_score')
  const enableSoilScore = checkAbility('soil_score')

  useEffect(() => {
    setCurrentTab('reports')
    getRanches()
  }, [])

  function hydraScoreColor(hydraScore) {
    if (hydraScore >= 93) return '#008000'
    else if (hydraScore >= 79) return '#8FB935'
    else if (hydraScore >= 68) return '#E6E22E'
    else if (hydraScore >= 48) return '#E09C3B'
    else if (hydraScore === 'NR') return '#808080'
    else return '#E64747'
  }

  function downloadReport(ranchName, report) {
    var endpoint = '/get_report_by_report_id'
    var params = {
      session_key: localAuth.sessionKey,
      report_id: report.report_id,
    }
    setLoading(true)
    callAPI(endpoint, params).then((res) => {
      setLoading(false)
      if (res.status === 'fail') {
        toast.warn(res.msg)
        return
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        logout()
        return
      }
      const downloadLink = document.createElement('a')
      downloadLink.href = res.pdf
      downloadLink.download =
        'Report - ' +
        ranchName +
        ' (' +
        new Date(report.created_time)
          .toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
          .replaceAll('/', '-') +
        ').pdf'
      downloadLink.click()
    })
  }

  const UpgradeHydraSoilScoreLayout = () => {
    if (isEmpty(ranchReports) && !enableRunReport) {
      return (
        <UpgradeLayout
          className="rounded-lg"
          title="Upgrade to generate and download reports."
          needUpgrade={true}
        />
      )
    }

    if (!enableHydraScore && !enableSoilScore) {
      return (
        <UpgradeLayout
          position="left-[18%] w-[18%]"
          className="min-h-0"
          title="Upgrade to view Hydra Score and Soil Scores."
          needUpgrade={true}
        />
      )
    }

    if (!enableHydraScore) {
      return (
        <UpgradeLayout
          position="left-[18%] w-[9%]"
          className="min-h-0"
          title="Upgrade to view Hydra Score."
          needUpgrade={true}
        />
      )
    }

    if (!enableSoilScore) {
      return (
        <UpgradeLayout
          position="left-[28%] w-[9%]"
          className="min-h-0"
          title="Upgrade to view Soil Scores."
          needUpgrade={true}
        />
      )
    }
  }

  if (isMobile) {
    return (
      <>
        {popupContent && (
          <InfoPopup
            content={popupContent}
            onClose={() => setPopupContent(null)}
          />
        )}
        {loading && <Spinner />}

        <div className="flex flex-col items-center mx-3 relative">
          <UpgradeLayout
            className="rounded-lg"
            title="Upgrade to generate and download reports."
            needUpgrade={isEmpty(ranchReports) && !enableRunReport}
          />
          {ranchReports.map((ranchReport, index) => (
            <div
              key={index}
              className="w-full bg-white p-4 mb-4 rounded-lg shadow-md"
            >
              <h3 className="text-center text-lg font-semibold mb-2">
                {ranchReport.ranch_name}
              </h3>
              <div className="flex justify-around items-center mb-2">
                {/* Hydra Score Section */}
                <div className="w-[6rem] text-center">
                  <div className="text-sm font-semibold mb-1">Hydra score</div>
                  <div
                    className="w-[4rem] mx-auto relative"
                    title={selectedReports[ranchReport.ranch_id].hover}
                  >
                    <UpgradeLockIconLayout
                      className="h-full rounded-full items-center justify-center opacity-80"
                      iconSize={{ width: 24, height: 24 }}
                      show={!enableHydraScore}
                    />
                    <CircularProgressbarWithChildren
                      value={
                        selectedReports[ranchReport.ranch_id].hydra_score ===
                        'NR'
                          ? 0
                          : parseInt(
                              selectedReports[ranchReport.ranch_id].hydra_score
                            )
                      }
                      background
                      backgroundPadding={6}
                      styles={buildStyles({
                        backgroundColor:
                          selectedReports[ranchReport.ranch_id].hydra_score ===
                          'NR'
                            ? hydraScoreColor(
                                selectedReports[ranchReport.ranch_id]
                                  .hydra_score
                              )
                            : hydraScoreColor(
                                parseInt(
                                  selectedReports[ranchReport.ranch_id]
                                    .hydra_score
                                )
                              ),
                        pathColor: '#fff',
                        trailColor: 'transparent',
                      })}
                    >
                      <strong
                        className="text-white text-xs"
                        style={{ fontSize: 16 }}
                      >
                        {selectedReports[ranchReport.ranch_id].hydra_score}
                      </strong>
                      {/* Info button positioned in top-right corner */}
                      {enableHydraScore && (
                        <button
                          style={{
                            height: '20px',
                            width: '20px',
                            position: 'absolute',
                            top: '-1px',
                            right: '-1px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            cursor: 'pointer',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            border: '1px solid #00ac9c',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                            transition:
                              'box-shadow 0.3s ease, transform 0.3s ease',
                          }}
                          onClick={() =>
                            setPopupContent(
                              selectedReports[ranchReport.ranch_id].hover
                            )
                          }
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.boxShadow =
                              '0 4px 10px rgba(0, 0, 0, 0.3)')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.boxShadow =
                              '0 2px 5px rgba(0, 0, 0, 0.2)')
                          }
                        >
                          <FaCircleInfo color="#00ac9c" />
                        </button>
                      )}
                    </CircularProgressbarWithChildren>
                  </div>
                </div>

                {/* Soil Score Section */}
                <div className="w-[6rem] text-center">
                  <div className="text-sm font-semibold mb-1">Soil score</div>
                  <div className="w-[4rem] mx-auto relative">
                    <UpgradeLockIconLayout
                      className="h-full rounded-full items-center justify-center opacity-80"
                      iconSize={{ width: 24, height: 24 }}
                      show={!enableSoilScore}
                    />
                    <CircularProgressbarWithChildren
                      value={
                        selectedReports[ranchReport.ranch_id].ss === 'NR'
                          ? 0
                          : parseInt(selectedReports[ranchReport.ranch_id].ss)
                      }
                      background
                      backgroundPadding={6}
                      styles={buildStyles({
                        backgroundColor: hydraScoreColor(
                          selectedReports[ranchReport.ranch_id].ss
                        ),
                        pathColor: '#fff',
                        trailColor: 'transparent',
                      })}
                    >
                      <strong
                        className="text-white text-xs"
                        style={{ fontSize: 14 }}
                      >
                        {selectedReports[ranchReport.ranch_id].ss}
                      </strong>
                    </CircularProgressbarWithChildren>
                  </div>
                </div>
              </div>

              <div className="text-center text-sm mb-2">
                Acreage: {selectedReports[ranchReport.ranch_id].ranch_acreage}{' '}
                ac
              </div>
              <div className="text-center text-sm mb-2">
                County:{' '}
                {JSON.parse(selectedReports[ranchReport.ranch_id].counties)
                  .map((item) => item.split(':')[0])
                  .join(', ')}
              </div>
              <Dropdown
                options={ranchReport.reports.map((report) => ({
                  value: report.created_time,
                  label: new Date(report.created_time).toLocaleDateString(
                    'en-US'
                  ),
                }))}
                placeholder="Select report"
                onChange={(e) => onReportDropdownChange(e, ranchReport)}
                value={{
                  value: selectedReports[ranchReport.ranch_id].created_time,
                  label: new Date(
                    selectedReports[ranchReport.ranch_id].created_time
                  ).toLocaleDateString('en-US'),
                }}
                className="w-full mb-2"
              />
              <button
                onClick={() =>
                  downloadReport(
                    ranchReport.ranch_name,
                    selectedReports[ranchReport.ranch_id]
                  )
                }
                className="flex justify-center items-center bg-blue-500 text-white px-4 py-2 rounded-lg w-full"
              >
                <IoMdDownload className="mr-2" /> Download Report
              </button>
            </div>
          ))}
        </div>
      </>
    )
  }
  return (
    <>
      {popupContent && (
        <InfoPopup
          content={popupContent}
          onClose={() => setPopupContent(null)}
        />
      )}
      {loading && <Spinner />}
      <div id="single-parcel-all" className="max-w-6xl px-3 mt-6 mx-auto">
        <div
          id="single-parcel-flood"
          className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg rounded-md overflow-visible transition-shadow duration-150 p-[5px] m-[10px]"
        >
          <div className="relative">
            <UpgradeHydraSoilScoreLayout />
            <table
              className="mt-2 mb-5"
              style={{
                width: '100%',
                tableLayout: 'fixed',
                position: 'relative',
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: '18%', textAlign: 'center' }}>
                    Ranch Name
                  </th>
                  <th style={{ width: '10%', textAlign: 'center' }}>
                    Hydra SCORE
                  </th>
                  <th style={{ width: '8%', textAlign: 'center' }}>
                    Soil SCORE
                  </th>
                  <th style={{ width: '8%', textAlign: 'center' }}>Acreage</th>
                  <th style={{ width: '18%', textAlign: 'center' }}>
                    County(ies)
                  </th>
                  <th style={{ width: '28%', textAlign: 'center' }}>
                    Date Generated
                  </th>
                  <th style={{ width: '8%', textAlign: 'center' }}>Download</th>
                </tr>
              </thead>
              <tbody>
                {ranchReports.map((ranchReport, index) => {
                  return (
                    <tr key={index}>
                      <td
                        className="items-center"
                        style={{ paddingLeft: '10px', textAlign: 'center' }}
                      >
                        {ranchReport.ranch_name}
                      </td>
                      <td
                        className="items-center"
                        style={{ textAlign: 'center' }}
                      >
                        <div
                          className={'w-[45px] mx-auto relative'}
                          title={selectedReports[ranchReport.ranch_id].hover}
                        >
                          <CircularProgressbarWithChildren
                            value={
                              selectedReports[ranchReport.ranch_id]
                                .hydra_score === 'NR'
                                ? 0
                                : parseInt(
                                    selectedReports[ranchReport.ranch_id]
                                      .hydra_score
                                  )
                            }
                            background
                            backgroundPadding={6}
                            styles={buildStyles({
                              backgroundColor:
                                selectedReports[ranchReport.ranch_id]
                                  .hydra_score === 'NR'
                                  ? hydraScoreColor(
                                      selectedReports[ranchReport.ranch_id]
                                        .hydra_score
                                    )
                                  : hydraScoreColor(
                                      parseInt(
                                        selectedReports[ranchReport.ranch_id]
                                          .hydra_score
                                      )
                                    ),
                              pathColor: '#fff',
                              trailColor: 'transparent',
                            })}
                          >
                            <strong
                              className="text-white text-xs"
                              style={{ fontSize: 16 }}
                            >
                              {
                                selectedReports[ranchReport.ranch_id]
                                  .hydra_score
                              }
                            </strong>
                            {/* Info button placed in the top-right corner */}
                            <button
                              style={{
                                height: '20px',
                                width: '20px',
                                position: 'absolute',
                                top: '-1px', // Adjust positioning based on appearance
                                right: '-1px', // Adjust positioning based on appearance
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                border: '1px solid #00ac9c',
                                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                                transition:
                                  'box-shadow 0.3s ease, transform 0.3s ease',
                              }}
                              onClick={() =>
                                setPopupContent(
                                  selectedReports[ranchReport.ranch_id].hover
                                )
                              }
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.boxShadow =
                                  '0 4px 10px rgba(0, 0, 0, 0.3)')
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.boxShadow =
                                  '0 2px 5px rgba(0, 0, 0, 0.2)')
                              }
                            >
                              <FaCircleInfo color="#00ac9c" />
                            </button>
                          </CircularProgressbarWithChildren>
                        </div>

                        {/* <div className={"w-[45px] mx-auto"} title={selectedReports[ranchReport.ranch_id].hover}>
                                                    <CircularProgressbarWithChildren
                                                        value={selectedReports[ranchReport.ranch_id].hydra_score === 'NR' ? 0 : parseInt(selectedReports[ranchReport.ranch_id].hydra_score)}
                                                        background
                                                        backgroundPadding={6}
                                                        styles={buildStyles({
                                                        backgroundColor: selectedReports[ranchReport.ranch_id].hydra_score === 'NR' ? hydraScoreColor(selectedReports[ranchReport.ranch_id].hydra_score) : hydraScoreColor(parseInt(selectedReports[ranchReport.ranch_id].hydra_score)),
                                                        pathColor: "#fff",
                                                        trailColor: "transparent"
                                                        })}>
                                                        <strong className="text-white text-xs" style={{ fontSize: 16}}>
                                                            {selectedReports[ranchReport.ranch_id].hydra_score}
                                                        </strong>
                                                    </CircularProgressbarWithChildren>
                                                </div>                                                */}
                      </td>
                      <td
                        className="items-center"
                        style={{ textAlign: 'center' }}
                      >
                        <div className={'w-[45px] mx-auto'}>
                          <CircularProgressbarWithChildren
                            value={
                              selectedReports[ranchReport.ranch_id].ss === 'NR'
                                ? 0
                                : parseInt(
                                    selectedReports[ranchReport.ranch_id].ss
                                  )
                            }
                            background
                            backgroundPadding={6}
                            styles={buildStyles({
                              backgroundColor:
                                selectedReports[ranchReport.ranch_id].ss ===
                                'NR'
                                  ? hydraScoreColor(
                                      selectedReports[ranchReport.ranch_id].ss
                                    )
                                  : hydraScoreColor(
                                      parseInt(
                                        selectedReports[ranchReport.ranch_id]
                                          .ss
                                      )
                                    ),
                              pathColor: '#fff',
                              trailColor: 'transparent',
                            })}
                          >
                            <strong
                              className="text-white text-xs"
                              style={{ fontSize: 16 }}
                            >
                              {selectedReports[ranchReport.ranch_id].ss}
                            </strong>
                          </CircularProgressbarWithChildren>
                        </div>
                      </td>
                      <td
                        className="items-center"
                        style={{ textAlign: 'center' }}
                      >
                        {selectedReports[ranchReport.ranch_id].ranch_acreage} ac
                      </td>
                      <td
                        className="items-center"
                        style={{ textAlign: 'center' }}
                      >
                        {JSON.parse(
                          selectedReports[ranchReport.ranch_id].counties
                        )
                          .map((item) => item.split(':')[0])
                          .join(', ')}
                      </td>
                      <td
                        className="items-center"
                        style={{ position: 'relative', textAlign: 'center' }}
                      >
                        <Dropdown
                          className="items-center text-center"
                          options={ranchReport.reports.map((report) => ({
                            value: report.created_time,
                            label: new Date(report.created_time)
                              .toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })
                              .replaceAll('/', '-'),
                          }))}
                          placeholder="Select your ranch"
                          onChange={(e) =>
                            onReportDropdownChange(e, ranchReport)
                          }
                          value={{
                            value:
                              selectedReports[ranchReport.ranch_id]
                                .created_time,
                            label: new Date(
                              selectedReports[ranchReport.ranch_id].created_time
                            )
                              .toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })
                              .replaceAll('/', '-'),
                          }}
                          menuPosition={'relative'}
                          menuPlacement={'bottom'}
                        />
                      </td>
                      <td
                        className="items-center"
                        style={{
                          paddingRight: '10px',
                          textAlign: 'center',
                          position: 'relative',
                          zIndex: 10,
                        }}
                      >
                        <div className={'w-[16px] mx-auto'}>
                          <div
                            className="cursor-pointer flex items-center justify-between text-[#457b9d] text-m font-medium"
                            onClick={() => {
                              downloadReport(
                                ranchReport.ranch_name,
                                selectedReports[ranchReport.ranch_id]
                              )
                            }}
                          >
                            <IoMdDownload />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}
